// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendTypeWithYBCRows, StrongOmit } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';
import { BendPreviewContextProvider } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';
import { BendCreatorFormContents } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorFormContents';
import {
  useBendCreatorFormContext,
  type ValidatedBendCreatorFormValues,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/Context/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/Context/useBendCreatorValidationContext';
import { getBendTypeWithYBCRows } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/helpers';

export type ValidatedFormData = StrongOmit<ValidatedBendCreatorFormValues, 'bendType' | 'ybcRows'> &
  BendTypeWithYBCRows;

type BendCreatorFormProps = {
  onClose: () => void;
  isEditing?: boolean;
  onSubmit: (v: ValidatedFormData) => void;
};

export const BendCreatorForm = ({ onClose, isEditing, onSubmit: parentOnSubmit }: BendCreatorFormProps) => {
  const form = useBendCreatorFormContext();

  const { lastValidation, isValidating } = useBendCreatorValidationContext();
  const { bendType: currentBendType, ybcRows: currentYbcRows } = form.values;
  const currentBendTypeWithRows = isNil(currentBendType)
    ? null
    : getBendTypeWithYBCRows({ bendType: currentBendType, ybcRows: currentYbcRows });
  // Rows only validate on blur, so pressing Enter in an edited cell would otherwise submit rows that were never checked.
  const validatedRowsAreCurrent = strongIsEqual(lastValidation?.request.ybcRows, currentBendTypeWithRows?.ybcRows);
  const serverSideValid = lastValidation?.isSuccess && !isValidating && validatedRowsAreCurrent;

  const handleSubmit = async (values: typeof form.values) => {
    // Validation ensures that all required fields will be non-null
    const { bendType, ybcRows, ...validatedValues } = values as ValidatedBendCreatorFormValues;
    const bendTypeWithRows = getBendTypeWithYBCRows({ bendType, ybcRows });
    if (!serverSideValid || isNil(bendTypeWithRows)) return;
    parentOnSubmit({ ...validatedValues, ...bendTypeWithRows });
  };

  return (
    <BendPreviewContextProvider>
      <form
        noValidate
        onSubmit={form.onSubmit(handleSubmit)}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <BendCreatorFormContents isEditing={isEditing} onClose={onClose} />
      </form>
    </BendPreviewContextProvider>
  );
};
